import React, { useMemo, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import {
  ChartBar, Coins, Users, Flame, Cube, Fingerprint, Hash, CircleNotch,
  PushPin, ArrowSquareOut, CaretDown, CaretUp, ChartPieSlice, ClockCounterClockwise, Lightning,
} from "@phosphor-icons/react";
import { cn, decodeAssetNameSmart, ipfsToHttp, truncMid, unixToDate, formatBigCount, formatRelativeTime } from "@/lib/format";
import { CopyBtn, Badge, AddressLink, TxHashCell } from "@/components/primitives";
import { Avatar, VerifiedTick, cip68Label, fmtQty, fmtQtyMeta } from "@/components/assets/parts";
import { useTokenMeta, useRequestMeta } from "@/components/assets/tokenMeta";
import { useWatchlist } from "@/components/assets/watchlist";
import { useAssetFeed, buildVolumeSeries, buildSupplyTimeline } from "@/components/assets/useAssetProfile";

const CARD = "relative overflow-hidden border border-primary/20 bg-card/40 backdrop-blur-md rounded-2xl box-glow";
const DONUT_PALETTE = ["#22d3ee", "#a855f7", "#6366f1", "#10b981", "#f59e0b", "#ec4899", "#38bdf8", "#f43f5e", "#14b8a6", "#eab308"];

const CardHead = ({ icon: Icon, title, right, accent = "cyan" }) => (
  <div className="px-5 py-3 border-b border-primary/15 flex items-center justify-between gap-3 flex-wrap">
    <span className={cn("inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] mono", accent === "violet" ? "text-[#c084fc]" : "text-[#22d3ee]")}>
      {Icon ? <Icon size={14} weight="duotone" /> : null}
      {title}
    </span>
    {right}
  </div>
);

const WindowToggle = ({ value, onChange, options, testid }) => (
  <div className="inline-flex border border-primary/15 rounded-lg overflow-hidden" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o}
        type="button"
        data-testid={`timeframe-toggle-${o.toLowerCase()}`}
        onClick={() => onChange(o)}
        className={cn(
          "px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] mono transition-colors",
          value === o ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground",
        )}
      >
        {o}
      </button>
    ))}
  </div>
);

const BusyState = ({ error, onRetry, height = 220, testid }) => (
  <div data-testid={testid} style={{ minHeight: height }} className="flex flex-col items-center justify-center gap-3 text-center px-6 py-8">
    <CircleNotch size={20} className="text-primary/40" />
    <div className="text-[12px] text-muted-foreground mono max-w-xs">{error?.message || "Temporarily unavailable."}</div>
    {onRetry ? (
      <button
        type="button"
        data-testid={testid ? `${testid}-retry` : undefined}
        onClick={onRetry}
        className="px-3 py-1.5 border border-primary/40 bg-primary/10 text-primary text-[10px] uppercase tracking-[0.18em] mono rounded hover:bg-primary/20"
      >
        Retry
      </button>
    ) : null}
  </div>
);

// ---------------- Identity card ----------------
const IdentityCard = ({ data, reg, name, logo, onOpenPolicy }) => {
  const { list, toggle } = useWatchlist();
  const [showHex, setShowHex] = useState(false);
  const watched = list.some((x) => x.asset === data.asset);
  const cip68 = cip68Label(data.asset_name);
  const std = data.onchain_metadata_standard;
  const isNft = cip68?.kind === "NFT" || std === "CIP25v1" && (data.onchain_metadata?.tokenType === "nft");
  const hexName = data.asset_name || "";

  return (
    <div data-testid="asset-identity-card" className={cn(CARD, "lg:col-span-4 flex flex-col")}>
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60 tracing-beam pointer-events-none" />
      <div className="p-5 flex flex-col gap-4 flex-1">
        <div className="flex items-start gap-4">
          {logo ? (
            <img
              data-testid="asset-identity-logo"
              src={logo}
              alt=""
              width={56}
              height={56}
              loading="lazy"
              className="w-14 h-14 rounded-xl object-cover ring-1 ring-[#22d3ee]/30 bg-black/30 shrink-0 shadow-[0_0_18px_rgba(34,211,238,0.18)]"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = "inline-flex"); }}
            />
          ) : null}
          <span style={{ display: logo ? "none" : "inline-flex" }}><Avatar item={data} size={56} testid="asset-identity-monogram" /></span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h1 data-testid="asset-identity-name" className="font-heading text-xl lg:text-2xl font-bold tracking-tight text-slate-50 truncate max-w-full">
                {name}
              </h1>
              {reg?.name ? <VerifiedTick testid="asset-identity-verified" /> : null}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              <Badge tone="info">{isNft ? "NFT" : "Token"}</Badge>
              {std ? <Badge tone="default">{std.replace("v1", " v1").toUpperCase()}</Badge> : <Badge tone="default">No on-chain metadata</Badge>}
              {cip68 ? (
                <span className="text-[9px] uppercase tracking-[0.08em] mono px-1.5 py-0.5 rounded bg-primary/15 text-primary/90 border border-primary/25">
                  CIP-68 · {cip68.kind}
                </span>
              ) : null}
              {reg?.ticker ? <span className="text-[10px] mono text-muted-foreground">${reg.ticker}</span> : null}
            </div>
          </div>
        </div>

        {(data.onchain_metadata?.description || reg?.name) ? (
          <p data-testid="asset-identity-description" className="text-[12px] text-muted-foreground font-sans leading-relaxed line-clamp-3">
            {data.onchain_metadata?.description
              ? (Array.isArray(data.onchain_metadata.description) ? data.onchain_metadata.description.join(" ") : String(data.onchain_metadata.description))
              : `${reg.name} — registered in the Cardano token registry.`}
          </p>
        ) : null}

        <div className="space-y-2.5 mt-1">
          <DataLine icon={Hash} label="Policy ID" value={data.policy_id} copy testid="asset-policy-id" />
          <DataLine icon={Fingerprint} label="Fingerprint" value={data.fingerprint} copy testid="asset-fingerprint" />
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground mono shrink-0">
              <Cube size={12} /> Asset name
            </span>
            <button
              type="button"
              data-testid="asset-name-hex-toggle"
              onClick={() => setShowHex((v) => !v)}
              className="inline-flex items-center gap-1 mono text-[12px] text-[#22d3ee] hover:underline min-w-0"
            >
              <span className="truncate">{showHex ? truncMid(hexName, 14, 8) || "—" : (decodeAssetNameSmart(hexName) || "unnamed")}</span>
              {showHex ? <CaretUp size={11} className="shrink-0" /> : <CaretDown size={11} className="shrink-0" />}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-auto pt-3 border-t border-primary/10">
          <button
            type="button"
            data-testid="watch-asset-button"
            data-pinned={watched ? "1" : "0"}
            onClick={() => toggle(data)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] mono transition-colors",
              watched ? "border-[#22d3ee]/60 text-[#22d3ee] bg-[#22d3ee]/[0.08]" : "border-primary/25 text-muted-foreground hover:text-foreground",
            )}
          >
            <PushPin size={12} weight={watched ? "fill" : "regular"} />
            {watched ? "Watching" : "Watch asset"}
          </button>
          {onOpenPolicy ? (
            <button
              type="button"
              data-testid="view-policy-assets-button"
              onClick={onOpenPolicy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] mono text-muted-foreground hover:text-foreground transition-colors"
            >
              <Cube size={12} /> Policy assets
            </button>
          ) : null}
          {data.fingerprint ? (
            <a
              data-testid="view-external-explorer-link"
              href={`https://cexplorer.io/asset/${data.fingerprint}`}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] mono text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowSquareOut size={12} /> Explorer
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const DataLine = ({ icon: Icon, label, value, copy, testid }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground mono shrink-0">
      {Icon ? <Icon size={12} /> : null} {label}
    </span>
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span data-testid={testid} className="mono text-[13px] tabnum text-foreground/90 truncate">{value ? truncMid(value, 10, 6) : "—"}</span>
      {copy && value ? <CopyBtn value={value} label={label.toLowerCase().replace(/\s+/g, "-")} /> : null}
    </span>
  </div>
);

// ---------------- Transaction volume chart ----------------
const VolumeTooltip = ({ active, payload, bucket }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-primary/30 bg-background/95 backdrop-blur px-3 py-2 text-[11px] mono">
      <div className="text-muted-foreground mb-0.5">{unixToDate(p.t)}</div>
      <div className="text-[#22d3ee]">{Number(p.count).toLocaleString("en-US")} tx{p.count === 1 ? "" : "s"}</div>
    </div>
  );
};

const VolumeChart = ({ asset }) => {
  const [win, setWin] = useState("7D");
  const { data, loading, error, reload, slow } = useAssetFeed(asset, "/transactions", { count: 50, order: "desc" });
  const txs = Array.isArray(data) ? data : (data?.items || []);
  const { points, bucket, total } = useMemo(() => buildVolumeSeries(txs, win), [txs, win]);

  const fmtTick = (t) => {
    const d = new Date(t * 1000);
    return bucket === "day"
      ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
      : `${String(d.getUTCHours()).padStart(2, "0")}h`;
  };
  const tickInterval = points.length > 8 ? Math.ceil(points.length / 8) : 0;

  return (
    <div data-testid="asset-volume-chart" className={cn(CARD, "lg:col-span-5 flex flex-col")}>
      <CardHead
        icon={ChartBar}
        title="Transaction volume"
        right={<div className="flex items-center gap-2">
          {loading ? <CircleNotch size={12} className="animate-spin text-primary/60" /> : null}
          <WindowToggle value={win} onChange={setWin} options={["24H", "7D", "30D", "ALL"]} testid="asset-volume-window" />
        </div>}
      />
      <div className="px-4 sm:px-5 pt-3 pb-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">transactions per {bucket}</span>
          <span className="text-[10px] mono text-muted-foreground/70">{formatBigCount(total)} in window · latest {txs.length} txs</span>
        </div>
        {error && !txs.length ? (
          <BusyState error={error} onRetry={reload} height={240} testid="asset-volume-busy" />
        ) : loading && !txs.length ? (
          <div className="h-[240px] flex flex-col items-center justify-center gap-2 text-[12px] text-muted-foreground mono text-center px-6">
            <div className="flex items-center"><CircleNotch size={16} className="animate-spin text-primary/70 mr-2" /> loading activity…</div>
            {slow ? <div className="text-[10px] text-muted-foreground/60 max-w-xs">High-activity token — this endpoint scans the full tx set and can take ~20–30s.</div> : null}
          </div>
        ) : !total ? (
          <div className="h-[240px] flex items-center justify-center text-[12px] text-muted-foreground mono text-center px-6">No transactions recorded in this window.</div>
        ) : (
          <div style={{ width: "100%", height: 240 }} className="grid-bg rounded-lg" data-testid="asset-volume-canvas">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="asset-vol-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(148,163,184,0.12)" vertical={false} />
                <XAxis dataKey="t" tickFormatter={fmtTick} tick={{ fill: "#64748b", fontSize: 10, fontFamily: "monospace" }} axisLine={{ stroke: "rgba(148,163,184,0.2)" }} tickLine={false} interval={tickInterval} minTickGap={18} />
                <YAxis allowDecimals={false} tickFormatter={(v) => formatBigCount(v)} tick={{ fill: "#64748b", fontSize: 10, fontFamily: "monospace" }} axisLine={false} tickLine={false} width={36} />
                <Tooltip content={<VolumeTooltip bucket={bucket} />} cursor={{ stroke: "rgba(34,211,238,0.3)" }} />
                <Area type="monotone" dataKey="count" stroke="#22d3ee" strokeWidth={2} fill="url(#asset-vol-area)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------- Holder concentration donut ----------------
const HolderDonut = ({ asset, totalSupply, feed }) => {
  const { data, loading, error, reload, slow } = feed;
  const rows = Array.isArray(data) ? data : (data?.items || []);
  const total = Number(totalSupply) || rows.reduce((s, r) => s + Number(r.quantity || 0), 0);

  const slices = useMemo(() => {
    if (!rows.length || !total) return [];
    const top = rows.slice(0, 8).map((r, i) => ({ name: `#${i + 1}`, value: Number(r.quantity) || 0 }));
    const topSum = top.reduce((s, d) => s + d.value, 0);
    const others = Math.max(0, total - topSum);
    const out = [...top];
    if (others > 0) out.push({ name: "Others", value: others });
    return out.map((d) => ({ ...d, share: total > 0 ? (d.value / total) * 100 : 0 }));
  }, [rows, total]);

  const topShare = slices.filter((s) => s.name !== "Others").reduce((a, s) => a + s.share, 0);

  return (
    <div data-testid="asset-holder-donut" className={cn("relative overflow-hidden border border-[#a855f7]/25 bg-card/40 backdrop-blur-md rounded-2xl box-glow lg:col-span-3 flex flex-col")}>
      <CardHead icon={ChartPieSlice} title="Holder mix" accent="violet"
        right={loading ? <CircleNotch size={12} className="animate-spin text-[#c084fc]/60" /> : null} />
      <div className="p-5 flex-1 flex flex-col items-center justify-center">
        {error && !rows.length ? (
          <BusyState error={error} onRetry={reload} height={200} testid="asset-holder-busy" />
        ) : loading && !rows.length ? (
          <div className="h-[200px] flex flex-col items-center justify-center gap-2 text-[12px] text-muted-foreground mono text-center px-4">
            <CircleNotch size={16} className="animate-spin text-[#c084fc]/70" />
            {slow ? <div className="text-[10px] text-muted-foreground/60 max-w-[12rem]">Heavy holder scan — large token, ~20–30s.</div> : null}
          </div>
        ) : !slices.length ? (
          <div className="h-[200px] flex items-center justify-center text-[12px] text-muted-foreground mono text-center">No holder data available.</div>
        ) : (
          <>
            <div style={{ width: "100%", height: 170 }} className="relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={slices} dataKey="value" nameKey="name" innerRadius={48} outerRadius={70} paddingAngle={2} stroke="none" isAnimationActive={false}>
                    {slices.map((s, i) => (
                      <Cell key={i} fill={s.name === "Others" ? "#334155" : DONUT_PALETTE[i % DONUT_PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0].payload;
                    return (
                      <div className="rounded-lg border border-[#a855f7]/30 bg-background/95 backdrop-blur px-3 py-2 text-[11px] mono">
                        <div className="text-[#c084fc]">{p.name === "Others" ? "Other holders" : `Holder ${p.name}`}</div>
                        <div className="text-muted-foreground">{p.share.toFixed(2)}% of supply</div>
                      </div>
                    );
                  }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mono">Top {Math.min(8, rows.length)}</div>
                <div className="text-lg font-bold font-mono text-slate-50">{topShare.toFixed(1)}%</div>
              </div>
            </div>
            <div className="mt-3 w-full text-center text-[10px] text-muted-foreground/70 mono">
              Top {Math.min(8, rows.length)} of {rows.length} indexed holders · UTXO addresses, not wallets
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------- Stat cards ----------------
const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="relative overflow-hidden bg-card/40 backdrop-blur-md border border-primary/15 hover:border-[#22d3ee]/30 rounded-xl p-4 transition-colors">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-lg border border-primary/25 bg-primary/[0.06] flex items-center justify-center shrink-0">
        <Icon size={15} className="text-primary" weight="duotone" />
      </div>
      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mono">{label}</span>
    </div>
    <div className="text-xl lg:text-2xl font-bold font-mono text-slate-50 tracking-tight break-all">{value}</div>
    {sub ? <div className="text-[10px] text-muted-foreground/70 mono mt-1">{sub}</div> : null}
  </div>
);

// ---------------- Top holders table ----------------
const TopHolders = ({ rows, total, loading, error, reload, slow }) => (
  <div data-testid="top-holders-table" className={cn(CARD, "lg:col-span-5")}>
    <CardHead icon={Users} title="Top holder distribution" />
    {error && !rows.length ? (
      <BusyState error={error} onRetry={reload} height={200} testid="top-holders-busy" />
    ) : loading && !rows.length ? (
      <div className="px-5 py-10 flex flex-col items-center justify-center gap-2 text-[12px] text-muted-foreground mono text-center">
        <div className="flex items-center"><CircleNotch size={16} className="animate-spin text-primary/70 mr-2" /> loading holders…</div>
        {slow ? <div className="text-[10px] text-muted-foreground/60 max-w-xs">High-activity token — holder ranking is a heavy upstream scan, ~20–30s.</div> : null}
      </div>
    ) : !rows.length ? (
      <div className="px-5 py-10 text-center text-[12px] text-muted-foreground mono">No holder data available.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mono bg-background/40">
              <th className="text-left px-5 py-2.5 font-medium">#</th>
              <th className="text-left px-3 py-2.5 font-medium">Address</th>
              <th className="text-right px-3 py-2.5 font-medium">Quantity</th>
              <th className="text-right px-5 py-2.5 font-medium">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 4).map((r, i) => {
              const share = total > 0 ? (Number(r.quantity) / total) * 100 : 0;
              return (
                <tr key={r.address || i} data-testid={`top-holder-row-${i}`} className="border-t border-primary/5 hover:bg-primary/[0.03]">
                  <td className="px-5 py-2.5 mono text-[12px] text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-2">
                      <AddressLink value={r.address} display={truncMid(r.address, 14, 8)} className="mono text-[12px] text-[#22d3ee]" />
                      <CopyBtn value={r.address} label="address" />
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right mono text-[12px] text-foreground/90">{fmtQty(r.quantity)}</td>
                  <td className="px-5 py-2.5 text-right">
                    <span className="inline-flex items-center gap-2 justify-end">
                      <span className="w-14 h-1.5 rounded-full bg-primary/10 overflow-hidden hidden sm:inline-block">
                        <span className="block h-full bg-[#22d3ee]" style={{ width: `${Math.min(100, share)}%` }} />
                      </span>
                      <span className="mono text-[12px] text-foreground/90 tabnum">{share < 0.01 ? "<0.01" : share.toFixed(2)}%</span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ---------------- Supply timeline ----------------
const TimelineTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-primary/30 bg-background/95 backdrop-blur px-3 py-2 text-[11px] mono">
      <div className="text-muted-foreground mb-0.5">{unixToDate(p.t)}</div>
      <div className="text-[#22d3ee]">supply {formatBigCount(p.supply)}</div>
      <div className={p.action === "burned" ? "text-rose-400" : "text-emerald-400"}>{p.action}</div>
    </div>
  );
};

const SupplyTimeline = ({ history, loading, error, reload }) => {
  const pts = useMemo(() => buildSupplyTimeline(history), [history]);
  const fixed = pts.length === 1;
  const chartData = fixed ? [{ ...pts[0] }, { ...pts[0], t: Math.floor(Date.now() / 1000) }] : pts;

  return (
    <div data-testid="supply-timeline-chart" className={cn(CARD, "lg:col-span-7 flex flex-col")}>
      <CardHead icon={ClockCounterClockwise} title="Supply timeline"
        right={fixed ? <span className="text-[9px] uppercase tracking-[0.15em] mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">Fixed supply</span> : (history?.length ? <span className="text-[10px] mono text-muted-foreground/70">{history.length} events</span> : null)} />
      <div className="px-4 sm:px-5 pt-3 pb-4 flex-1 flex flex-col">
        {error && !history?.length ? (
          <BusyState error={error} onRetry={reload} height={220} testid="supply-timeline-busy" />
        ) : loading && !history?.length ? (
          <div className="h-[220px] flex items-center justify-center text-[12px] text-muted-foreground mono"><CircleNotch size={16} className="animate-spin text-primary/70 mr-2" /> loading…</div>
        ) : !pts.length ? (
          <div className="h-[220px] flex items-center justify-center text-[12px] text-muted-foreground mono">No mint/burn history available.</div>
        ) : (
          <div style={{ width: "100%", height: 220 }} data-testid="supply-timeline-canvas">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="supply-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(148,163,184,0.12)" vertical={false} />
                <XAxis dataKey="t" tickFormatter={(t) => new Date(t * 1000).toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" })} tick={{ fill: "#64748b", fontSize: 10, fontFamily: "monospace" }} axisLine={{ stroke: "rgba(148,163,184,0.2)" }} tickLine={false} minTickGap={30} />
                <YAxis tickFormatter={(v) => formatBigCount(v)} tick={{ fill: "#64748b", fontSize: 10, fontFamily: "monospace" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip content={<TimelineTooltip />} cursor={{ stroke: "rgba(168,85,247,0.3)" }} />
                <Area type="stepAfter" dataKey="supply" stroke="#a855f7" strokeWidth={2} fill="url(#supply-area)" isAnimationActive={false} dot={fixed ? { r: 3, fill: "#a855f7" } : false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------- Recent supply events ----------------
const RecentEvents = ({ history, loading, error, reload }) => (
  <div data-testid="recent-supply-events" className={cn(CARD, "lg:col-span-12")}>
    <CardHead icon={Lightning} title="Recent supply events" />
    {error && !history?.length ? (
      <BusyState error={error} onRetry={reload} height={120} testid="recent-events-busy" />
    ) : loading && !history?.length ? (
      <div className="px-5 py-8 flex items-center justify-center text-[12px] text-muted-foreground mono"><CircleNotch size={16} className="animate-spin text-primary/70 mr-2" /> loading…</div>
    ) : !history?.length ? (
      <div className="px-5 py-8 text-center text-[12px] text-muted-foreground mono">No mint or burn events found.</div>
    ) : (
      <div className="divide-y divide-primary/5">
        {history.slice(0, 8).map((e, i) => (
          <div key={e.tx_hash || i} data-testid={`supply-event-${i}`} className="flex items-center justify-between gap-3 px-5 py-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <Badge tone={e.action === "minted" ? "success" : "warn"}>{e.action}</Badge>
              <span className={cn("mono text-[13px] tabnum", e.action === "minted" ? "text-emerald-400" : "text-rose-400")}>
                {e.action === "minted" ? "+" : "−"} {fmtQty(e.amount)}
              </span>
            </div>
            <div className="flex items-center gap-4 text-[11px] mono text-muted-foreground">
              <span className="hidden sm:inline">block {Number(e.block_height).toLocaleString("en-US")}</span>
              <span title={unixToDate(e.block_time)}>{formatRelativeTime(e.block_time)}</span>
              <TxHashCell value={e.tx_hash} len={10} />
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ---------------- Root ----------------
export const AssetProfile = ({ data, onOpenPolicy }) => {
  const asset = data?.asset;
  useRequestMeta([asset]);
  const { meta } = useTokenMeta();
  const holderFeed = useAssetFeed(asset, "/addresses", { count: 12, order: "desc" });
  const historyFeed = useAssetFeed(asset, "/history", { count: 50, order: "desc" });
  if (!data) return null;
  const reg = asset ? meta.get(asset) : null;

  const name = reg?.name || reg?.ticker || decodeAssetNameSmart(data.asset_name || "")
    || (data.fingerprint ? `${data.fingerprint.slice(0, 18)}…` : "Unnamed asset");
  const onchainImg = ipfsToHttp(data.onchain_metadata?.image)
    || ipfsToHttp(data.onchain_metadata?.files && data.onchain_metadata.files[0]?.src);
  const logo = reg?.logo || onchainImg || null;

  const holderRows = Array.isArray(holderFeed.data) ? holderFeed.data : (holderFeed.data?.items || []);
  const history = Array.isArray(historyFeed.data) ? historyFeed.data : (historyFeed.data?.items || []);
  const total = Number(data.quantity) || holderRows.reduce((s, r) => s + Number(r.quantity || 0), 0);

  const genesis = history.length ? history[history.length - 1] : null;

  return (
    <div data-testid="asset-profile" className="space-y-6">
      {/* top zone */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
        <IdentityCard data={data} reg={reg} name={name} logo={logo} onOpenPolicy={onOpenPolicy} />
        <VolumeChart asset={data.asset} />
        <HolderDonut asset={data.asset} totalSupply={data.quantity} feed={holderFeed} />
      </div>

      {/* stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-circulating-supply" icon={Coins} label="Circulating supply" value={fmtQtyMeta(data.quantity, reg)} sub="decimal-adjusted on-chain" />
        <StatCard testid="stat-holders-loaded" icon={Users} label="Holders loaded" value={holderFeed.loading && !holderRows.length ? "…" : (holderRows.length ? holderRows.length.toLocaleString("en-US") : "—")} sub="indexed sample (bounded)" />
        <StatCard testid="stat-mint-burn" icon={Flame} label="Mint / burn events" value={data.mint_or_burn_count != null ? Number(data.mint_or_burn_count).toLocaleString("en-US") : "—"} sub="policy operations" />
        <StatCard testid="stat-first-minted" icon={Cube} label="First minted" value={genesis ? new Date(genesis.block_time * 1000).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }) : "—"} sub={genesis ? `block ${Number(genesis.block_height).toLocaleString("en-US")}` : "genesis"} />
      </div>

      {/* overview lower section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <TopHolders rows={holderRows} total={total} loading={holderFeed.loading} error={holderFeed.error} reload={holderFeed.reload} slow={holderFeed.slow} />
        <SupplyTimeline history={history} loading={historyFeed.loading} error={historyFeed.error} reload={historyFeed.reload} />
        <RecentEvents history={history} loading={historyFeed.loading} error={historyFeed.error} reload={historyFeed.reload} />
      </div>
    </div>
  );
};
